if (!customElements.get('accordion-group')) {
  class Accordion extends HTMLElement {
    constructor() {
      super();
      this.handleToggle = this.handleToggle.bind(this);
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('accordion-item')).filter(
        (item) => !item.parentElement?.closest('accordion-item')
      );

      this.items.forEach((item) => {
        item.addEventListener('toggle', this.handleToggle);
      });

      this.items
        .filter((item) => item.hasAttribute('is-open'))
        .slice(1)
        .forEach((item) => {
          item.removeAttribute('is-open');
          item.closeAccordion?.();
        });
    }

    disconnectedCallback() {
      this.items.forEach((item) => {
        item.removeEventListener('toggle', this.handleToggle);
      });
    }

    handleToggle(event) {
      const openItem = event.target;

      if (!this.items.includes(openItem) || !openItem.isOpen) return;

      this.items.forEach((item) => {
        if (item !== openItem) {
          item.closeAccordion();
        }
      });
    }
  }

  customElements.define('accordion-group', Accordion);
}

if (!customElements.get('accordion-item')) {
  class AccordionItem extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.toggleAccordion = this.toggleAccordion.bind(this);
      this.updateHeight = this.updateHeight.bind(this);
      this.debouncedUpdateHeight = debounce(() => {
        this.updateHeight();
        this.syncInert();
      }, ON_CHANGE_DEBOUNCE_TIMER);
    }

    connectedCallback() {
      this.button = this.querySelector('button');
      this.content = this.querySelector('.content');
      this.icon = this.querySelector('.icon');

      if (this.button && this.content) {
        if (!this.content.id) {
          AccordionItem.autoIdCount += 1;
          this.content.id = `AccordionContent-${AccordionItem.autoIdCount}`;
        }
        this.button.setAttribute('aria-controls', this.content.id);
        this.button.addEventListener('click', this.toggleAccordion);
      }

      this.addEventListener('read-more:toggled', this.updateHeight);

      // Check initial open state based on attribute
      this.isOpen = this.hasAttribute('is-open');
      this.renderState();

      window.addEventListener('resize', this.debouncedUpdateHeight);

      this.contentInner = this.content?.firstElementChild;
      if (this.contentInner) {
        this.contentResizeObserver = new ResizeObserver(() => {
          if (this.isOpen) this.updateHeight();
        });
        this.contentResizeObserver.observe(this.contentInner);
      }
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.toggleAccordion);
      this.removeEventListener('read-more:toggled', this.updateHeight);
      window.removeEventListener('resize', this.debouncedUpdateHeight);
      this.contentResizeObserver?.disconnect();
    }

    toggleAccordion() {
      if (!this.isCollapsible()) return;

      AccordionItem.suppressScrollAnchoring(AccordionItem.getScrollParent(this));
      this.isOpen = !this.isOpen;
      this.renderState();
      this.dispatchEvent(new Event('toggle', { bubbles: true }));
    }

    closeAccordion() {
      this.isOpen = false;
      this.renderState();
    }

    updateHeight() {
      if (!this.isOpen) return;

      this.content.style.transitionProperty = 'none';
      this.content.style.maxHeight = `${this.content.scrollHeight}px`;

      requestAnimationFrame(() => {
        this.content.style.transitionProperty = '';
      });
    }

    isCollapsible() {
      return getComputedStyle(this.content).maxHeight !== 'none';
    }

    syncInert() {
      this.content.inert = !this.isOpen && this.isCollapsible();
    }

    renderState() {
      this.content.style.transitionProperty = '';
      this.content.style.maxHeight = this.isOpen
        ? `${this.content.scrollHeight}px`
        : '0';
      this.syncInert();
      this.button?.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');

      if (this.icon) {
        this.icon.classList.toggle('rotate-180', this.isOpen);
        this.icon.classList.toggle('rotate-0', !this.isOpen);
        this.icon.dataset.state = this.isOpen ? 'open' : 'close';
      }
      this.classList.toggle('is-open', this.isOpen);
      this.classList.toggle('is-close', !this.isOpen);

      if (this.isOpen) {
        this.setAttribute('is-open', '');
      } else {
        this.removeAttribute('is-open');
      }
    }

    static suppressScrollAnchoring(container) {
      if (!container) return;
      clearTimeout(AccordionItem.scrollAnchorRestoreTimers.get(container));
      container.style.overflowAnchor = 'none';
      AccordionItem.scrollAnchorRestoreTimers.set(
        container,
        setTimeout(() => {
          container.style.overflowAnchor = '';
          AccordionItem.scrollAnchorRestoreTimers.delete(container);
        }, AccordionItem.SCROLL_ANCHOR_SUPPRESS_MS)
      );
    }

    static getScrollParent(element) {
      let node = element.parentElement;
      while (node) {
        const { overflowY } = getComputedStyle(node);
        if (
          (overflowY === 'auto' || overflowY === 'scroll') &&
          node.scrollHeight > node.clientHeight
        ) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    }
  }

  AccordionItem.autoIdCount = 0;
  AccordionItem.SCROLL_ANCHOR_SUPPRESS_MS = 400;
  AccordionItem.scrollAnchorRestoreTimers = new WeakMap();

  customElements.define('accordion-item', AccordionItem);
}
